import React from 'react';
import PropTypes from 'prop-types';
import { Button, Progress, Space, Tag, Typography } from 'antd';
import { metricName, metricValue } from './presentation';
import {
  COMMUNICATION_ASSERTION_LABELS,
  COMMUNICATION_CATEGORY_LABELS,
  COMMUNICATION_SOURCE_LABELS,
  COMMUNICATION_STATUS_LABELS,
  COMMUNICATION_TAG_LABELS,
  COURSE_CATEGORY_LABELS,
  DIMENSION_LABELS,
  SCORE_STATUS_LABELS,
  TRAINING_MANNER_LABELS,
  capabilityChangeText,
  capabilityConfidenceText,
  capabilityScore10,
  capabilityScoreText,
  capabilitySourceText,
  confidenceText,
  durationText,
  metricDifferenceText,
  readableRecordStatus,
  textOrDash,
  timeText,
} from './SimpleWorkspaceSupport';
import styles from './SimpleWorkspace.less';

const { Text } = Typography;

const numberValue = (value) => Number(value) || 0;

function EntityCell({ title, meta }) {
  return (
    <div className={styles.entityCell}>
      <Text strong className={styles.entityName}>
        {textOrDash(title)}
      </Text>
      {meta && <Text className={styles.entityMeta}>{meta}</Text>}
    </div>
  );
}

EntityCell.propTypes = {
  title: PropTypes.node,
  meta: PropTypes.node,
};

EntityCell.defaultProps = {
  title: null,
  meta: null,
};

function TimeCell({ value, prefix }) {
  const formatted = timeText(value);
  if (formatted === '-') {
    return <Text type="secondary">暂无记录</Text>;
  }
  const [date, clock] = formatted.split(/\s+/);
  return (
    <div className={styles.timeCell}>
      {prefix && <Text className={styles.timeLabel}>{prefix}</Text>}
      <Text>{date}</Text>
      {clock && <Text className={styles.timeClock}>{clock}</Text>}
    </div>
  );
}

TimeCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  prefix: PropTypes.string,
};

TimeCell.defaultProps = {
  value: null,
  prefix: '',
};

const capabilityStatusColor = (status) => {
  if (
    status === 'available' ||
    status === 'training_confirmed' ||
    status === 'training_inferred' ||
    status === 'motion_measured' ||
    status === 'mixed_evidence'
  )
    return 'green';
  if (status === 'training_observed') return 'blue';
  if (status === 'not_calibrated') return 'gold';
  if (
    status === 'insufficient_coverage' ||
    status === 'insufficient_measurements'
  ) {
    return 'orange';
  }
  return undefined;
};

const capabilityChangeColor = (record) => {
  if (record.changeType === 'increased') return 'green';
  if (record.changeType === 'decreased') return 'red';
  if (record.changeType === 'initial') return 'blue';
  return undefined;
};

export default function buildWorkspaceColumns({
  actionNames,
  courseTitles,
  openActionDetail,
  openActionProfile,
  openCourseDetail,
  openCapabilityHistory,
  setSelectedMetricSeries,
}) {
  const actionColumns = [
    { title: '训练时间', dataIndex: 'ctime', render: timeText },
    {
      title: '动作',
      dataIndex: 'actionId',
      render: (value) => actionNames[value] || (value ? `动作 ${value}` : '-'),
    },
    {
      title: '所属课程',
      dataIndex: 'roomId',
      render: (value) => courseTitles[value] || (value ? `课程 ${value}` : '-'),
    },
    {
      title: '评估状态',
      dataIndex: 'status',
      render: (value) => <Tag>{readableRecordStatus(value)}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => openActionDetail(record)}>
          查看
        </Button>
      ),
    },
  ];

  const actionProfileColumns = [
    {
      title: '动作',
      dataIndex: 'actionId',
      width: 210,
      render: (value) => (
        <EntityCell
          title={actionNames[value] || (value ? `动作 ${value}` : '-')}
          meta={value ? `动作 ID ${value}` : null}
        />
      ),
    },
    {
      title: '训练记录',
      width: 155,
      render: (_, record) => (
        <div className={styles.metricCell}>
          <div className={styles.metricPrimary}>
            <strong>{numberValue(record.trainingCount)}</strong>
            <span>次</span>
          </div>
          <div className={styles.metricSecondary}>
            <span>完成 {numberValue(record.completedCount)}</span>
            <span>跳过 {numberValue(record.skippedCount)}</span>
          </div>
        </div>
      ),
    },
    {
      title: '评估覆盖',
      width: 165,
      render: (_, record) => {
        const evaluationCount = numberValue(record.evaluationCount);
        const succeededCount = numberValue(record.succeededCount);
        const percent = evaluationCount
          ? Math.min(100, Math.round((succeededCount / evaluationCount) * 100))
          : 0;
        return (
          <div className={styles.coverageCell}>
            <div className={styles.metricPrimary}>
              <strong>{evaluationCount}</strong>
              <span>次评估</span>
            </div>
            <Progress
              percent={percent}
              showInfo={false}
              strokeColor="#2d83b8"
              trailColor="#e7eef3"
              size="small"
            />
            <Text className={styles.metricHint}>
              已完成 {succeededCount} 次
            </Text>
          </div>
        );
      },
    },
    {
      title: '偏好反馈',
      width: 190,
      render: (_, record) => (
        <Space size={[6, 6]} wrap className={styles.preferenceTags}>
          <Tag color="green">喜欢 {numberValue(record.explicitLikeCount)}</Tag>
          <Tag color="red">
            不喜欢 {numberValue(record.explicitDislikeCount)}
          </Tag>
        </Space>
      ),
    },
    {
      title: '评估跨度',
      width: 220,
      render: (_, record) => (
        <div className={styles.dateRangeCell}>
          <TimeCell value={record.firstEvaluatedAt} prefix="首次" />
          <TimeCell value={record.latestEvaluatedAt} prefix="最近" />
        </div>
      ),
    },
    {
      title: '数据状态',
      width: 175,
      render: (_, record) => (
        <Space
          size={[4, 6]}
          direction="vertical"
          className={styles.statusCluster}
        >
          {numberValue(record.comparableEvaluationCount) >= 2 ? (
            <Tag color="blue">
              {numberValue(record.comparableEvaluationCount)} 次可比较
            </Tag>
          ) : (
            <Tag>可比较数据不足</Tag>
          )}
          <Tag color={record.latestStatus ? 'green' : undefined}>
            {readableRecordStatus(record.latestStatus)}
          </Tag>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 76,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => openActionProfile(record)}>
          查看
        </Button>
      ),
    },
  ];

  const actionMetricColumns = [
    {
      title: '观察指标',
      render: (_, record) => metricName(record),
    },
    {
      title: '有效记录',
      render: (_, record) => `${record.points?.length || 0} 次`,
    },
    {
      title: '首次记录',
      render: (_, record) =>
        metricValue(record.points?.[0]?.value, record.unit, record.metricCode),
    },
    {
      title: '最近记录',
      render: (_, record) =>
        metricValue(
          record.points?.slice(-1)[0]?.value,
          record.unit,
          record.metricCode,
        ),
    },
    {
      title: '首末差值',
      render: (_, record) => metricDifferenceText(record),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => setSelectedMetricSeries(record)}>
          查看趋势
        </Button>
      ),
    },
  ];

  const courseColumns = [
    {
      title: '课程 ID',
      dataIndex: 'roomId',
      width: 105,
      render: (value) => <Text strong>{textOrDash(value)}</Text>,
    },
    {
      title: '上课时间',
      width: 210,
      render: (_, record) => (
        <div className={styles.courseTimeCell}>
          <TimeCell
            value={
              record.actualStartTime ||
              record.trainingStartTime ||
              record.scheduledStartTime
            }
          />
          <Text className={styles.roomCode}>
            房间号 {textOrDash(record.roomNo)}
          </Text>
        </div>
      ),
    },
    {
      title: '课程信息',
      width: 255,
      render: (_, record) => (
        <div className={styles.courseInfoCell}>
          <Button
            type="link"
            onClick={() => openCourseDetail(record)}
            className={styles.courseTitleButton}
          >
            {textOrDash(record.title)}
          </Button>
          <Space size={[6, 6]} wrap className={styles.courseMetaTags}>
            {TRAINING_MANNER_LABELS[record.trainingManner] && (
              <Tag color="blue">
                {TRAINING_MANNER_LABELS[record.trainingManner]}
              </Tag>
            )}
            <Tag color="orange">
              {COURSE_CATEGORY_LABELS[record.category] || '其他课程'}
            </Tag>
          </Space>
        </div>
      ),
    },
    {
      title: '训练情况',
      width: 230,
      render: (_, record) => {
        const hasCompletion =
          record.completeActionPercentage !== null &&
          record.completeActionPercentage !== undefined;
        const completion = hasCompletion
          ? numberValue(record.completeActionPercentage)
          : 0;
        return (
          <div className={styles.trainingProgressCell}>
            <div className={styles.trainingDurationRow}>
              <Text>训练时长</Text>
              <Text strong>{durationText(record.classDurationSeconds)}</Text>
            </div>
            <div className={styles.trainingCompletionRow}>
              <Text>动作完成</Text>
              <Text strong>
                {hasCompletion ? `${completion}%` : '暂无记录'}
              </Text>
            </div>
            <Progress
              percent={Math.max(0, Math.min(100, completion))}
              showInfo={false}
              strokeColor={completion > 100 ? '#d89614' : '#2d83b8'}
              trailColor="#e7eef3"
              size="small"
            />
          </div>
        );
      },
    },
    {
      title: '课表状态',
      width: 105,
      render: () => (
        <Tag color="green" className={styles.courseStatusTag}>
          已完课
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 76,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => openCourseDetail(record)}>
          查看
        </Button>
      ),
    },
  ];

  const capabilityColumns = [
    {
      title: '运动能力',
      dataIndex: 'dimensionCode',
      width: 220,
      render: (value) => (
        <EntityCell title={DIMENSION_LABELS[value] || '其他能力'} />
      ),
    },
    {
      title: '当前分值',
      width: 185,
      render: (_, record) => {
        const score = capabilityScore10(record);
        return score !== null ? (
          <div className={styles.capabilityScoreCell}>
            <div>
              <strong>{capabilityScoreText(record)}</strong>
              <span>/ 10</span>
            </div>
            <Progress
              percent={Math.max(0, Math.min(100, score * 10))}
              showInfo={false}
              strokeColor="#2d83b8"
              trailColor="#e7eef3"
              size="small"
            />
            <Text type="secondary">
              {record.confirmedScore === null ||
              record.confirmedScore === undefined
                ? '训练确认：待积累'
                : `训练确认：${(
                    Math.round((Number(record.confirmedScore) / 10) * 10) / 10
                  ).toFixed(1)} / 10`}
            </Text>
          </div>
        ) : (
          <Text type="secondary">暂未评分</Text>
        );
      },
    },
    {
      title: '评估状态',
      dataIndex: 'scoreStatus',
      width: 145,
      render: (value) => (
        <Tag color={capabilityStatusColor(value)}>
          {SCORE_STATUS_LABELS[value] || '状态已记录'}
        </Tag>
      ),
    },
    {
      title: '判断依据',
      width: 185,
      render: (_, record) => (
        <div className={styles.capabilityEvidenceCell}>
          <Text strong>{capabilitySourceText(record.scoreSource)}</Text>
          <Text type="secondary">
            {capabilityConfidenceText(record.confidenceLevel)}
          </Text>
        </div>
      ),
    },
    {
      title: '训练支撑',
      width: 180,
      render: (_, record) => (
        <div className={styles.capabilitySupportCell}>
          <span>{numberValue(record.relevantCourseCount)} 节课</span>
          <span>{numberValue(record.distinctActionCount)} 个动作</span>
          <span>{numberValue(record.independentWeekCount)} 个训练周</span>
          <span>跨度 {numberValue(record.trainingSpanDays)} 天</span>
          <span>{numberValue(record.evidenceCount)} 条证据</span>
        </div>
      ),
    },
    {
      title: '本次变化',
      width: 120,
      render: (_, record) => (
        <Tag color={capabilityChangeColor(record)}>
          {capabilityChangeText(record)}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      width: 190,
      render: (_, record) => (
        <TimeCell value={record.windowEndedAt || record.ctime} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 88,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => openCapabilityHistory(record)}>
          查看变化
        </Button>
      ),
    },
  ];

  const capabilityHistoryColumns = [
    {
      title: '评估时间',
      width: 175,
      render: (_, record) => (
        <TimeCell value={record.windowEndedAt || record.ctime} />
      ),
    },
    {
      title: '能力分',
      width: 95,
      render: (_, record) => {
        const value = capabilityScoreText(record);
        return value === '-' ? '-' : `${value} / 10`;
      },
    },
    {
      title: '变化',
      width: 105,
      render: (_, record) => (
        <Tag color={capabilityChangeColor(record)}>
          {capabilityChangeText(record)}
        </Tag>
      ),
    },
    {
      title: '判断依据',
      width: 140,
      render: (_, record) => capabilitySourceText(record.scoreSource),
    },
    {
      title: '训练支撑',
      width: 210,
      render: (_, record) =>
        `${numberValue(record.relevantCourseCount)} 节课 · ${numberValue(
          record.distinctActionCount,
        )} 个动作 · ${numberValue(
          record.independentWeekCount,
        )} 周 · 跨度 ${numberValue(record.trainingSpanDays)} 天 · ${numberValue(
          record.evidenceCount,
        )} 条证据`,
    },
    {
      title: '可信程度',
      dataIndex: 'confidenceLevel',
      width: 110,
      render: capabilityConfidenceText,
    },
  ];

  const communicationColumns = [
    {
      title: '发生时间',
      dataIndex: 'occurredAt',
      width: 190,
      render: timeText,
    },
    {
      title: '来源',
      dataIndex: 'sourceType',
      width: 130,
      render: (value) => (
        <Tag color={value === 'workwx_chat' ? 'green' : 'blue'}>
          {COMMUNICATION_SOURCE_LABELS[value] || '训练沟通'}
        </Tag>
      ),
    },
    {
      title: '观察内容',
      dataIndex: 'summary',
      render: (value, record) => (
        <div className={styles.communicationSummary}>
          <Text>{textOrDash(value)}</Text>
          <Text type="secondary" className={styles.communicationMeta}>
            {COMMUNICATION_ASSERTION_LABELS[record.assertionType] ||
              '训练相关信息'}
          </Text>
        </div>
      ),
    },
    {
      title: '分类与标签',
      width: 200,
      render: (_, record) => (
        <Space size={[4, 4]} wrap>
          <Tag>{COMMUNICATION_CATEGORY_LABELS[record.category] || '其他'}</Tag>
          <Tag color="orange">
            {COMMUNICATION_TAG_LABELS[record.tagType] || '训练信息'}
          </Tag>
        </Space>
      ),
    },
    {
      title: '当前状态',
      dataIndex: 'status',
      width: 110,
      render: (value) => COMMUNICATION_STATUS_LABELS[value] || '待确认',
    },
    {
      title: '可信程度',
      dataIndex: 'confidence',
      width: 100,
      render: (value) => confidenceText(value),
    },
  ];

  return {
    actionColumns,
    actionProfileColumns,
    actionMetricColumns,
    courseColumns,
    capabilityColumns,
    capabilityHistoryColumns,
    communicationColumns,
  };
}
